import { useEffect } from 'react';

import { useSetLeaseDetailsSubmitLock } from '@/modules/application-tracker/components/lease-form';

import {
  useInviteEligibilityCheck,
  type InviteEligibilityRowFilter,
} from '../hooks/use-invite-eligibility-check';
import { useTrackInviteEligibilityCheck } from '../hooks/use-invite-eligibility-tracking';

type InviteEligibilityControllerProps = {
  /**
   * Defaults to every client-valid email (create invite).
   * Edit invite passes `includeEditInviteEligibilityRow` so unchanged recipients stay out.
   * Pass a stable function — an inline function resets the debounce on every render.
   */
  includeRow?: InviteEligibilityRowFilter;
  /** Edit invite passes the invite id so the invite does not match its own rows. */
  inviteAlphaId?: string;
};

/**
 * Mounts once inside the invite form (FormProvider) to own the eligibility
 * query side effects — syncing block outcomes onto email fields as RHF errors,
 * and check telemetry.
 * Banners read the same React Query cache via {@link useInviteEligibilityCheck}.
 */
export function InviteEligibilityController({
  includeRow,
  inviteAlphaId,
}: InviteEligibilityControllerProps) {
  const { hasBlock, preflight, shownOutcome } = useInviteEligibilityCheck({
    syncFieldErrors: true,
    includeRow,
    inviteAlphaId,
  });
  const setSubmitLocked = useSetLeaseDetailsSubmitLock();
  useTrackInviteEligibilityCheck({ preflight, shownOutcome });

  useEffect(() => {
    setSubmitLocked(hasBlock);
    return () => setSubmitLocked(false);
  }, [hasBlock, setSubmitLocked]);

  return null;
}
